import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import {
  getBetHistory,
  getEventDetail,
  listOpenEvents,
  placeBet,
  type PredictionBetSlip,
  type PredictionEvent,
  type PredictionPaymentType,
  type PredictionSelection,
} from '../services/predictionService'
import ElectionResultsLiveBoard from '../components/prediction/ElectionResultsLiveBoard'
import { mapPopularityToElectionRows, ELECTION_DISPLAY_TOP_N } from '../components/prediction/electionBoardUtils'

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const getCurrentPoints = (userInfo: ReturnType<typeof useUserInfo>['userInfo']): number => {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  return 0
}

const PredictionPage: React.FC = () => {
  const { themeConfig } = useTenantThemeConfig()
  const { user } = useAuth()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()

  const [events, setEvents] = useState<PredictionEvent[]>([])
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PredictionEvent | null>(null)
  const [history, setHistory] = useState<PredictionBetSlip[]>([])
  const [selectedSelectionId, setSelectedSelectionId] = useState('')
  const [stake, setStake] = useState('100')
  const [paymentType, setPaymentType] = useState<PredictionPaymentType>('cash')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tab, setTab] = useState<'bet' | 'history'>('bet')

  const points = getCurrentPoints(userInfo)
  const balanceText = walletData ? formatCurrency(walletData.balance.main) : '0.00'

  const allowedPaymentTypes = useMemo(
    () => detail?.allowedPaymentTypes || ['cash', 'points'],
    [detail?.allowedPaymentTypes]
  )

  const loadEvents = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listOpenEvents()
      const list = res.data || []
      setEvents(list)
      if (!selectedEventId && list[0]?._id) {
        setSelectedEventId(list[0]._id)
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดรายการไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    } finally {
      setLoading(false)
    }
  }, [selectedEventId])

  const loadDetail = useCallback(async (eventId: string) => {
    try {
      const res = await getEventDetail(eventId)
      setDetail(res.data)
      if (res.data.allowedPaymentTypes?.length) {
        setPaymentType(res.data.allowedPaymentTypes[0])
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดรายละเอียดไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    }
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const res = await getBetHistory({ limit: 50 })
      setHistory(res.data || [])
    } catch {
      setHistory([])
    }
  }, [])

  useEffect(() => {
    void loadEvents()
    void loadHistory()
  }, [loadEvents, loadHistory])

  useEffect(() => {
    if (selectedEventId) {
      void loadDetail(selectedEventId)
    }
  }, [selectedEventId, loadDetail])

  useEffect(() => {
    if (!selectedEventId || tab !== 'bet') return undefined
    const timer = window.setInterval(() => {
      void loadDetail(selectedEventId)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [selectedEventId, tab, loadDetail])

  const selectedSelection = useMemo(
    () => detail?.selections?.find((s) => s._id === selectedSelectionId) || null,
    [detail?.selections, selectedSelectionId]
  )

  const stakeNum = Number(stake)
  const potentialPayout = selectedSelection && Number.isFinite(stakeNum)
    ? Math.round(stakeNum * selectedSelection.currentOdds * 100) / 100
    : 0

  const handlePlaceBet = async () => {
    if (!user || !detail || !selectedSelectionId) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาเลือกตัวเลือก', confirmButtonColor: '#f59e0b' })
      return
    }

    const amount = Number(stake)
    if (!Number.isFinite(amount) || amount <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุยอดแทง', confirmButtonColor: '#f59e0b' })
      return
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแทง',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <div><b>งาน:</b> ${detail.title}</div>
          <div><b>เลือก:</b> ${selectedSelection?.label || '-'}</div>
          <div><b>ยอด:</b> ${formatNumber(amount)} ${paymentType === 'points' ? 'คะแนน' : 'บาท'}</div>
          <div><b>Odds:</b> ${selectedSelection?.currentOdds || '-'}</div>
          <div><b>จ่ายถ้าถูก:</b> ${formatNumber(potentialPayout)}</div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#16a34a',
    })

    if (!confirm.isConfirmed) return

    setSubmitting(true)
    try {
      const res = await placeBet({
        eventId: detail._id,
        selectionId: selectedSelectionId,
        stake: amount,
        paymentType,
      })

      await Swal.fire({
        icon: 'success',
        title: 'แทงสำเร็จ',
        text: `เลขที่โพย ${res.data.slip.slipId}`,
        confirmButtonColor: '#16a34a',
      })

      setSelectedSelectionId('')
      await Promise.all([loadDetail(detail._id), loadHistory(), refreshWallet(), refreshUserInfo()])
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แทงไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const renderPopularity = (event: PredictionEvent) => {
    const popularity = event.popularity
    if (!popularity?.selections?.length) return null

    const rows = mapPopularityToElectionRows(popularity, ELECTION_DISPLAY_TOP_N)

    return (
      <ElectionResultsLiveBoard
        title={event.title}
        footerTitle={event.title}
        subtitle={`${event.eventCode} · Top ${ELECTION_DISPLAY_TOP_N} · realtime`}
        rows={rows}
        totalStake={popularity.totalStake}
        totalBets={popularity.totalBets}
        totalCandidates={popularity.selections.length}
        maxVisible={ELECTION_DISPLAY_TOP_N}
        live
      />
    )
  }

  const renderSelectionCard = (sel: PredictionSelection) => {
    const isActive = selectedSelectionId === sel._id
    return (
      <button
        key={sel._id}
        type="button"
        onClick={() => setSelectedSelectionId(sel._id)}
        className={`btn w-100 text-start mb-2 ${isActive ? 'btn-warning' : 'btn-outline-light'}`}
        style={{ borderColor: sel.barColor || undefined }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {sel.imageUrl && <img src={sel.imageUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />}
            <div>
              <div className="fw-bold">{sel.badgeLabel ? `${sel.badgeLabel} ` : ''}{sel.label}</div>
              {sel.subtitle && <div className="small opacity-75">{sel.subtitle}</div>}
            </div>
          </div>
          <div className="text-end">
            <div className="fw-bold">{sel.currentOdds}</div>
            <div className="small">odds</div>
          </div>
        </div>
      </button>
    )
  }

  if (themeConfig.settings.predictionEnabled !== true) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center">
          <h5 className="mb-2">ยังไม่เปิดใช้งานทายผล (Prediction)</h5>
          <p className="mb-3 small text-muted">
            ฟีเจอร์นี้ยังไม่ได้เปิดให้ tenant ของคุณ — ติดต่อผู้ดูแลระบบหากต้องการใช้งาน
          </p>
          <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">กลับหน้าหลัก</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h4 className="mb-1 c-white">ทายผล (Prediction)</h4>
          <div className="small c-white opacity-75">
            ยอดเงิน {balanceText} · คะแนน {formatNumber(points)} pts
          </div>
        </div>
        <div className="btn-group">
          <button type="button" className={`btn btn-sm ${tab === 'bet' ? 'btn-warning' : 'btn-outline-light'}`} onClick={() => setTab('bet')}>แทง</button>
          <button type="button" className={`btn btn-sm ${tab === 'history' ? 'btn-warning' : 'btn-outline-light'}`} onClick={() => setTab('history')}>ประวัติ</button>
        </div>
      </div>

      {tab === 'history' ? (
        <div className="card bg-dark border-secondary">
          <div className="card-body">
            {history.length === 0 ? (
              <div className="text-center text-muted py-4">ยังไม่มีประวัติการแทง</div>
            ) : (
              history.map((slip) => (
                <div key={slip.slipId} className="border-bottom border-secondary py-2">
                  <div className="d-flex justify-content-between">
                    <strong>{slip.eventTitle}</strong>
                    <span className="badge bg-secondary">{slip.status}</span>
                  </div>
                  <div className="small">{slip.selectionLabel} · odds {slip.lockedOdds}</div>
                  <div className="small">
                    แทง {formatNumber(slip.stake)} {slip.paymentType === 'points' ? 'pts' : 'บาท'}
                    {' · '}จ่าย {formatNumber(slip.payoutAmount ?? slip.potentialPayout)}
                  </div>
                  <div className="small text-muted">{new Date(slip.createdAt).toLocaleString('th-TH')} · {slip.slipId}</div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="card bg-dark border-secondary h-100">
              <div className="card-body">
                <h6 className="text-warning mb-3">งานที่เปิดรับ</h6>
                {loading && <div className="text-muted">กำลังโหลด...</div>}
                {events.map((event) => (
                  <button
                    key={event._id}
                    type="button"
                    onClick={() => setSelectedEventId(event._id)}
                    className={`btn w-100 text-start mb-2 ${selectedEventId === event._id ? 'btn-warning' : 'btn-outline-light'}`}
                  >
                    <div className="fw-bold">{event.title}</div>
                    <div className="small opacity-75">{event.eventCode} · {event.category}</div>
                  </button>
                ))}
                {events.length === 0 && !loading && (
                  <div className="text-muted">ยังไม่มีงานที่เปิดรับ</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            {!detail ? (
              <div className="card bg-dark border-secondary"><div className="card-body text-muted">เลือกงานทางซ้าย</div></div>
            ) : (
              <div className="card bg-dark border-secondary">
                <div className="card-body">
                  <h5 className="text-warning">{detail.title}</h5>
                  {detail.description && <p className="small text-muted">{detail.description}</p>}

                  {renderPopularity(detail)}

                  <h6 className="mb-2">เลือกตัวเลือก</h6>
                  {(detail.selections || []).map(renderSelectionCard)}

                  <div className="row g-2 mt-3">
                    <div className="col-md-4">
                      <label className="form-label small">ยอดแทง</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={stake}
                        onChange={(e) => setStake(e.target.value)}
                        min={detail.minStake || 1}
                        max={detail.maxStake || undefined}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">ชำระด้วย</label>
                      <select
                        className="form-select form-select-sm"
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value as PredictionPaymentType)}
                      >
                        {allowedPaymentTypes.includes('cash') && <option value="cash">เงินสด</option>}
                        {allowedPaymentTypes.includes('points') && <option value="points">คะแนน</option>}
                      </select>
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="small w-100">
                        จ่ายถ้าถูก: <strong className="text-warning">{formatNumber(potentialPayout)}</strong>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-warning mt-3"
                    disabled={submitting || !selectedSelectionId}
                    onClick={() => void handlePlaceBet()}
                  >
                    {submitting ? 'กำลังส่งโพย...' : 'ยืนยันแทง'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PredictionPage
